/**
 * Package room selector — faithful to Figma:
 * PC (≥1200): wide gallery, active center card bordered + check on chosen,
 * side arrows (white/gold), caption under active card.
 * Mobile: single image + dots + gold Choose button.
 * Selections (or first-plan preselection) → EuroPackageState.
 */
class EuroPackageRoom extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.sectionId || '';
    this.roomTitle = this.dataset.roomTitle || '';
    this.preselectFirst = this.dataset.preselectFirst !== 'false';

    // Zebra background parity among room sections (in page order).
    // Runs synchronously during parse (before first paint) so it never flashes.
    const order = Array.from(document.querySelectorAll('euro-package-room')).indexOf(this);
    this.dataset.zebra = (order + 1) % 2 === 0 ? 'even' : 'odd';

    this.swiperEl = this.querySelector('[data-package-room-swiper]');
    this.prevBtn = this.querySelector('[data-package-room-prev]');
    this.nextBtn = this.querySelector('[data-package-room-next]');
    this.dotsEl = this.querySelector('[data-package-room-pagination]');
    this.selectBtns = Array.from(this.querySelectorAll('[data-package-room-select]'));
    this.resultEl = this.querySelector('[data-package-room-result]');
    this.resultTitleEl = this.querySelector('[data-package-room-result-title]');
    this.resultSummaryEl = this.querySelector('[data-package-room-result-summary]');
    this.slides = Array.from(this.querySelectorAll('[data-package-plan]'));

    this.swiper = null;
    this.activeIndex = 0;
    this.selectedPlanId = null;
    this.preselected = false;

    this.onSelectClick = () => this.confirmActivePlan();
    this.onSlideClick = (event) => {
      // caption/choose 按钮自己处理确认，卡片点击不重复触发
      if (event.target.closest('[data-package-room-select]')) return;
      const slide = event.currentTarget;
      this.selectByIndex(this.slides.indexOf(slide));
    };
    this.onResize = () => this.handleResize();

    this.selectBtns.forEach((btn) => btn.addEventListener('click', this.onSelectClick));
    this.slides.forEach((slide) => slide.addEventListener('click', this.onSlideClick));
    window.addEventListener('resize', this.onResize);

    this.whenReady(() => {
      this.initSwiper();
      this.restoreOrPreselect();
      this.syncUI();
      window.EuroAOS?.refresh();
    });
  }

  disconnectedCallback() {
    this.selectBtns.forEach((btn) => btn.removeEventListener('click', this.onSelectClick));
    this.slides.forEach((slide) => slide.removeEventListener('click', this.onSlideClick));
    window.removeEventListener('resize', this.onResize);
    clearInterval(this.readyTimer);
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }
    if (this.sectionId && window.EuroPackageState) {
      window.EuroPackageState.clear(this.sectionId);
    }
  }

  whenReady(callback) {
    if (window.Swiper) {
      callback();
      return;
    }
    this.readyTimer = setInterval(() => {
      if (!window.Swiper) return;
      clearInterval(this.readyTimer);
      callback();
    }, 30);
  }

  isDesktop() {
    return window.matchMedia('(min-width: 1200px)').matches;
  }

  initSwiper() {
    if (!this.swiperEl || !window.Swiper || this.slides.length === 0) return;

    if (this.slides.length <= 1) {
      this.swiperEl.classList.add('euro-package-room__swiper--single');
      this.slides[0].classList.add('is-active');
      return;
    }

    const desktopSpace = (20 / 1920) * window.innerWidth;

    this.swiper = new window.Swiper(this.swiperEl, {
      slidesPerView: 1,
      spaceBetween: 0,
      speed: 450,
      watchOverflow: true,
      centeredSlides: false,
      pagination: this.dotsEl
        ? {
            el: this.dotsEl,
            clickable: true,
            bulletClass: 'euro-package-room__dot',
            bulletActiveClass: 'is-active',
          }
        : undefined,
      navigation: {
        prevEl: this.prevBtn || undefined,
        nextEl: this.nextBtn || undefined,
      },
      breakpoints: {
        1200: {
          slidesPerView: 'auto',
          centeredSlides: false,
          spaceBetween: desktopSpace,
        },
      },
      on: {
        slideChange: (swiper) => {
          this.activeIndex = swiper.realIndex;
          this.syncUI();
        },
      },
    });

    this.activeIndex = this.swiper.realIndex || 0;
  }

  handleResize() {
    if (!this.swiper) return;
    if (this.isDesktop()) {
      this.swiper.params.spaceBetween = (20 / 1920) * window.innerWidth;
    }
    this.swiper.update();
  }

  getPlanData(slide) {
    if (!slide) return null;
    let items = [];
    try {
      items = JSON.parse(slide.dataset.planItems || '[]');
    } catch (err) {
      items = [];
    }
    return {
      planId: slide.dataset.planId || '',
      planTitle: slide.dataset.planTitle || '',
      planImage: slide.dataset.planImage || null,
      planOverviewImage: slide.dataset.planOverviewImage || null,
      summary: slide.dataset.planSummary || '',
      items: items,
    };
  }

  syncUI() {
    this.slides.forEach((slide, index) => {
      const active = index === this.activeIndex;
      const chosen = slide.dataset.planId === this.selectedPlanId;
      slide.classList.toggle('is-active', active);
      slide.classList.toggle('is-chosen', chosen);
    });
  }

  selectByIndex(index) {
    if (index < 0 || index >= this.slides.length) return;

    if (this.swiper) {
      // 把目标卡作为当前预览卡
      this.activeIndex = index;
      if (this.swiper.realIndex !== index) {
        this.swiper.slideTo(index, 300);
      }
    } else {
      this.activeIndex = index;
    }
    this.confirmActivePlan();
  }

  confirmActivePlan() {
    const slide = this.slides[this.activeIndex];
    const data = this.getPlanData(slide);
    if (!data || !data.planId || !this.sectionId || !window.EuroPackageState) return;

    this.selectedPlanId = data.planId;
    this.renderResult(data);
    this.syncUI();

    window.EuroPackageState.set(this.sectionId, {
      roomTitle: this.roomTitle,
      planId: data.planId,
      planTitle: data.planTitle,
      planImage: data.planImage,
      planOverviewImage: data.planOverviewImage,
      summary: data.summary,
      items: data.items,
    });
  }

  renderResult(data) {
    if (!this.resultEl) return;
    if (!data) {
      this.resultEl.hidden = true;
      return;
    }
    if (this.resultTitleEl) this.resultTitleEl.textContent = data.planTitle || '';
    if (this.resultSummaryEl) {
      this.resultSummaryEl.innerHTML = this.formatSummaryHtml(data.items, data.summary);
    }
    this.resultEl.hidden = false;
  }

  formatSummaryHtml(items, fallback) {
    const esc = (value) =>
      String(value == null ? '' : value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');

    if (Array.isArray(items) && items.length) {
      return items
        .map((item, index) => {
          const qty = item.quantity != null ? item.quantity : 1;
          const label = esc(qty) + ' ' + esc(item.title || '');
          const sep =
            index < items.length - 1
              ? '<span class="euro-package-room__sep" aria-hidden="true">|</span>'
              : '';
          return '<span class="euro-package-room__item">' + label + '</span>' + sep;
        })
        .join('');
    }
    return fallback ? '<span class="euro-package-room__item">' + esc(fallback) + '</span>' : '';
  }

  restoreOrPreselect() {
    const saved = window.EuroPackageState
      ? window.EuroPackageState.get(this.sectionId)
      : null;

    if (saved && saved.planId) {
      const index = this.slides.findIndex((slide) => slide.dataset.planId === saved.planId);
      if (index >= 0) {
        this.selectedPlanId = saved.planId;
        this.renderResult(saved);
        if (this.swiper) {
          this.swiper.slideTo(index, 0);
          this.activeIndex = index;
        } else {
          this.activeIndex = index;
        }
        this.preselected = true;
        return;
      }
    }

    if (this.preselectFirst && this.slides.length) {
      const first = this.slides[0];
      const data = this.getPlanData(first);
      if (data && data.planId && window.EuroPackageState) {
        this.selectedPlanId = data.planId;
        this.renderResult(data);
        this.preselected = true;
        window.EuroPackageState.set(this.sectionId, {
          roomTitle: this.roomTitle,
          planId: data.planId,
          planTitle: data.planTitle,
          planImage: data.planImage,
          planOverviewImage: data.planOverviewImage,
          summary: data.summary,
          items: data.items,
        });
      }
    }
  }
}

if (!customElements.get('euro-package-room')) {
  customElements.define('euro-package-room', EuroPackageRoom);
}
